import * as React from "react"
import { createRef, useEffect, useMemo, useState } from "react"

import { debounce } from "lodash-es"
import { twMerge } from "tailwind-merge"

import { STORE_PRODUCT_GROUP_DEFAULT_LABEL } from "~/constants"
import { Bee } from "~/src/components/BeeKit"

export type Tab = {
  label: string
  ref: React.RefObject<HTMLDivElement | null>
  onClick?: () => void
}

export type CategoryTabBarProps = {
  tabs: Tab[]
  scrollRoot: React.RefObject<HTMLDivElement | null>
  scrollOffset?: number
  className?: string
  containerClassName?: string
  kind?: "primary" | "secondary"
  initialIndex?: number
}

export function CategoryTabBar({
  tabs,
  scrollRoot,
  className,
  containerClassName,
  kind,
  initialIndex,
  scrollOffset = 0,
}: CategoryTabBarProps) {
  const filteredTabsWithIndex = tabs
    .map((tab, index) => {
      return { tab, index }
    })
    .filter(({ tab }) => tab.label !== STORE_PRODUCT_GROUP_DEFAULT_LABEL)
  const filteredTabs = filteredTabsWithIndex.map(({ tab }) => tab)
  const filteredInitialIndex = filteredTabsWithIndex.findIndex(({ index }) => index == initialIndex)
  const tabHeaders = useMemo(() => filteredTabs.map(() => createRef<HTMLButtonElement>()), [tabs])
  const [selectedIndex, setSelectedIndex] = useState(0)

  const calcSelectedIndex = () => {
    if (filteredTabs.length < 1) {
      return
    }

    const scrollRootTop = scrollRoot.current ? scrollRoot.current.getBoundingClientRect().top : 0
    const scrollTop = scrollRootTop + scrollOffset
    const firstVisibleIndex = filteredTabs.findIndex((tab) => {
      if (!tab.ref.current) {
        return false
      }

      const tabBottom = tab.ref.current.getBoundingClientRect().bottom
      return tabBottom >= scrollTop
    })

    if (firstVisibleIndex === -1) {
      setSelectedIndex(filteredTabs.length - 1)
    } else {
      setSelectedIndex(firstVisibleIndex)
    }
  }

  // Scroll tabs into view when user scrolls down on the page
  // (ex. when the screen is smaller & the tabs on the right are not visible)
  useEffect(() => {
    const tabHeader = tabHeaders[selectedIndex]?.current
    if (tabHeader) {
      tabHeader.scrollIntoView({ block: "nearest" })
    }
  }, [selectedIndex])

  // Observe scrolling changes
  useEffect(() => {
    const options: IntersectionObserverInit = {
      root: scrollRoot.current,
      rootMargin: `-${scrollOffset}px 0px 0px 0px`,
    }

    // Debounce the interaction handler to prevent multiple calls to calcSelectedIndex
    const observer = new IntersectionObserver(debounce(calcSelectedIndex, 1), options)
    filteredTabs.forEach(({ ref }) => {
      if (ref.current) {
        observer.observe(ref.current)
      }
    })
    return () => {
      filteredTabs.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current)
        }
      })
    }
  }, [filteredTabs, scrollRoot, scrollOffset])

  const scrollToContent = (tab: Tab) => {
    // requestAnimationFrame is used to ensure the scrollIntoView is called after React has updated the DOM, without
    // this the scrollIntoView may not work as expected
    requestAnimationFrame(() => {
      tab.ref.current?.scrollIntoView({ block: "start" })
    })
  }

  // Initial index scroll
  useEffect(() => {
    if (filteredInitialIndex >= 0) {
      const tab = filteredTabs[filteredInitialIndex]
      if (tab) {
        scrollToContent(tab)
      }
    }
  }, [initialIndex])

  return (
    <div className={twMerge("sticky", containerClassName)}>
      <Bee.TabBar kind={kind} className={className}>
        {filteredTabs.map((tab, index) => (
          <Bee.Tab
            ref={tabHeaders[index]}
            kind={kind}
            selected={index === selectedIndex}
            onClick={() => {
              scrollToContent(tab)
              if (tab.ref.current) {
                tab.onClick?.()
              }
            }}
            key={index}
          >
            {tab.label}
          </Bee.Tab>
        ))}
      </Bee.TabBar>
    </div>
  )
}
